/// <amd-module name="Wit/DeliveryPlans/PlanSettingsPanel/Components/PlanSettingsPanel" />

import * as React from "react";
import { IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { newGuid } from "VSS/Core/Util/String";
import { useComponentContext, WrappedComponent } from "VSS/Platform/Layout";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import {
    CardFieldSettings,
    CardSettings,
    DeliveryViewPropertyCollection,
    FieldInfo,
    FilterClause,
    Marker,
    PlanType,
    PlanUserPermissions,
    Rule,
    TimelineTeamData,
    UpdatePlan
} from "Wit/Clients/Work/Contracts/Work";
import { ISettingsView, IWitSettingsPanelProps } from "Wit/Common/WitSettingsPanelTypes/Components/WitSettingsPanel.props";
import { ICriteriaSelectedSettingData } from "Wit/Common/WitSettingsViews/Components/CriteriaTable/CriteriaInterfaces";
import {
    getCriteriaSelectedSettingData,
    getFieldCriteriaAsFilterClauses,
    ICriteriaService
} from "Wit/Common/WitSettingsViews/Components/CriteriaTable/CriteriaService";
import { CriteriaSettingsView } from "Wit/Common/WitSettingsViews/Components/CriteriaTable/CriteriaSettingsView";
import { IStylesRuleData } from "Wit/Common/WitSettingsViews/Components/StylesTable/StyleInterfaces";
import { getStyleSettingData, getStyleSettingsAsStyleRules, IStyleService } from "Wit/Common/WitSettingsViews/Components/StylesTable/StyleService";
import { getStyleSettingsTab } from "Wit/Common/WitSettingsViews/Components/StylesTable/StyleTable";
import { ITagStyleData } from "Wit/Common/WitSettingsViews/Components/TagStylesTable/TagStylesInterfaces";
import {
    getTagStyleSettingsAsRules,
    getTagStylesFromRules,
    getTagStylesSettingsView
} from "Wit/Common/WitSettingsViews/Components/TagStylesTable/TagStylesTable";
import { toWebApiFieldType } from "Wit/Common/WitSettingsViews/Utils/FieldUtils";
import { IFieldReferenceSetting, IFieldSettings } from "Wit/Common/WitSettingsViewsTypes/Components/FieldSettingsView.props";
import { IWorkItemFieldService } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService";
import { IWorkItemFieldService2 } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";
import { BoardConstants } from "Wit/DeliveryPlans/Board/BoardConstants";
import { PlanData } from "Wit/DeliveryPlans/Common/Client/Contracts/DataProvider";
import { PlanNameAndDescriptionConstants, PlanNameAndDescriptionInput } from "Wit/DeliveryPlans/Common/Components/PlanNameAndDescriptionInput";
import {
    getTeamSettingsAsTeamBacklogMapping,
    IDeliveryPlansTeamSettingsService
} from "Wit/DeliveryPlans/Common/Components/TeamSettingsTable/DeliveryPlansTeamSettingsService";
import { ITeamSelectedSettingData } from "Wit/DeliveryPlans/Common/Components/TeamSettingsTable/TeamSettingsInterfaces";
import { TeamSettingsTable } from "Wit/DeliveryPlans/Common/Components/TeamSettingsTable/TeamSettingsTable";
import { IPlanService } from "Wit/DeliveryPlans/Common/Services/IPlanService";
import { hasPermission } from "Wit/DeliveryPlans/Common/Utils/PermissionUtils";
import * as Resources from "Wit/DeliveryPlans/Resources";
import { SettingsViewConstants } from "../PlanSettingsConstants";
import { IMarkerSettingData } from "../PlanSettingsInterfaces";
import { DeliveryPlansFieldSettingsView, forEachSetting } from "./DeliveryPlansFieldSettingsView";
import { getMarkerSettingsAsMarkers, MarkerSettingsView } from "./MarkerSettingsView";

export interface PlanSettingsPanelProps {
    cardFieldSettings: CardFieldSettings;
    fieldCriteria: FilterClause[];
    markerSettings: Marker[];
    styleSettings: Rule[];
    tagStyleSettings: Rule[];
    onDismiss: () => void;
    plan: PlanData;
    teams: TimelineTeamData[];
}

export const PlanSettingsPanel: React.FunctionComponent<PlanSettingsPanelProps> = ({
    cardFieldSettings,
    fieldCriteria,
    markerSettings,
    styleSettings,
    tagStyleSettings,
    onDismiss,
    plan,
    teams
}) => {
    const pageContext = useComponentContext().pageContext;
    const editable = hasPermission(plan.userPermissions, PlanUserPermissions.Edit);

    const criteriaService = pageContext.getService<ICriteriaService>("ICriteriaService");
    const teamSettingsService = pageContext.getService<IDeliveryPlansTeamSettingsService>("IDeliveryPlansTeamSettingsService");
    const styleService = pageContext.getService<IStyleService>("IStyleService");

    // set up overview observables
    const isOverviewDirty = new ObservableValue(false);
    const isOverviewValid = new ObservableValue(true);
    const description = plan.description || "";
    const descriptionObservable = new ObservableValue(description);
    const nameObservable = new ObservableValue(plan.name);

    // set up teams view
    const teamSettingsData = getTeamSelectedSettingData(teams);
    teamSettingsService.addTeamSettingsRows(teamSettingsData);

    // set up field criteria view
    const criteriaSettingsData = getCriteriaSelectedSettingData(fieldCriteria);
    criteriaService.addCriteriaSettingsRows(criteriaSettingsData);

    // set up markers view
    const markers = new ObservableArray<IMarkerSettingData>(getMarkerSettingData(markerSettings));
    const isMarkersDataDirty = new ObservableValue(false);
    const isMarkersDataValid = new ObservableValue(true);

    // set up fields view
    const originalAdditionalFields = cardFieldSettings.additionalFields?.map(field => field.referenceName) || [];
    const isFieldsDataDirty = new ObservableValue(false);
    const isFieldsDataValid = new ObservableValue(true);
    const coreFields: { [fieldKey: string]: IObservableValue<boolean> } = {};
    forEachSetting(cardFieldSettings, (key, value) => {
        coreFields[key] = new ObservableValue(value);
    });

    const fieldSettings: IFieldSettings = {
        additionalFields: new ObservableArray<IFieldReferenceSetting>(
            cardFieldSettings.additionalFields?.map(field => {
                return { id: newGuid(), referenceName: field.referenceName };
            })
        ),
        coreFields
    };

    // set up settings view
    const stylesSettingsData = getStyleSettingData(styleSettings);
    const tagStylesSettings = new ObservableArray<ITagStyleData>(getTagStylesFromRules(tagStyleSettings));

    // define the settings views
    const settingsViews: ISettingsView[] = [
        {
            isDirty: isOverviewDirty,
            isValid: isOverviewValid,
            name: SettingsViewConstants.overview,
            renderContent: () => (
                <PlanNameAndDescriptionInput
                    className="flex-column flex-grow padding-horizontal-20 rhythm-vertical-16"
                    description={descriptionObservable}
                    disabled={!editable}
                    name={nameObservable}
                    setIsDirty={(currentDescription, currentName) => {
                        const dirty = currentName !== plan.name || currentDescription !== description;
                        if (dirty !== isOverviewDirty.value) {
                            isOverviewDirty.value = dirty;
                        }
                    }}
                    setIsValid={(currentDescription, currentName) => {
                        const valid = !!currentName;
                        if (valid !== isOverviewValid.value) {
                            isOverviewValid.value = valid;
                        }
                    }}
                />
            )
        },
        {
            isDirty: teamSettingsService.isDirty,
            isValid: teamSettingsService.isValid,
            name: SettingsViewConstants.teams,
            renderContent: () => <TeamSettingsTable disabled={!editable} showHeader />
        },
        {
            isDirty: criteriaService.isDirty,
            isValid: criteriaService.isValid,
            name: SettingsViewConstants.fieldCriteria,
            renderContent: () => <CriteriaSettingsView className="delivery-plans-settings-table" disabled={!editable} />
        },
        {
            isDirty: isMarkersDataDirty,
            isValid: isMarkersDataValid,
            name: SettingsViewConstants.markers,
            renderContent: () => (
                <MarkerSettingsView
                    disabled={!editable}
                    isValid={isMarkersDataValid}
                    markers={markers}
                    setIsDirty={currentMarkerSettings => {
                        const dirty = areMarkersDirty(currentMarkerSettings, markerSettings);
                        if (dirty != isMarkersDataDirty.value) {
                            isMarkersDataDirty.value = dirty;
                        }
                    }}
                />
            )
        },
        {
            isDirty: isFieldsDataDirty,
            isValid: isFieldsDataValid,
            name: SettingsViewConstants.fields,
            renderContent: () => (
                <DeliveryPlansFieldSettingsView
                    {...fieldSettings}
                    disabled={!editable}
                    isDirty={isFieldsDataDirty}
                    isValid={isFieldsDataValid}
                    labels={fieldResourceStrings}
                    originalAdditionalFields={originalAdditionalFields}
                    originalCardFieldSettings={cardFieldSettings}
                />
            )
        },
        getStyleSettingsTab(pageContext, stylesSettingsData, !editable),
        getTagStylesSettingsView(tagStylesSettings, !editable)
    ];

    const onClose = () => {
        teamSettingsService.clear();
        criteriaService.clear();
        styleService.clear();
        onDismiss();
    };

    const onSave = async () => {
        const planService = pageContext.getService<IPlanService>("IPlanService");
        const fieldService = isFeatureFlagEnabled(pageContext, FeatureFlags.UseNewWorkItemFieldService, false)
                    ? pageContext.getService<IWorkItemFieldService2>("IWorkItemFieldService2")
                    : pageContext.getService<IWorkItemFieldService>("IWorkItemFieldService");

        // settings data may not have been initialized if that tab wasn't clicked on
        const updatedTeamSettingsData = teamSettingsService.isDirty.value ? teamSettingsService.rows.value : teamSettingsData;
        const updatedCriteriaSettingsData = criteriaService.isDirty.value ? criteriaService.rows.value : criteriaSettingsData;

        const planUpdate = getPlanUpdate(
            plan,
            nameObservable.value,
            descriptionObservable.value,
            fieldService,
            fieldSettings,
            updatedCriteriaSettingsData,
            updatedTeamSettingsData,
            markers.value,
            styleService.rows.value,
            tagStylesSettings.value
        );

        await planService.updatePlan(plan.id, planUpdate);
        return true;
    };

    return (
        <WrappedComponent<IWitSettingsPanelProps>
            dependencies={["ms.vss-work-web.wit-settings-panel"]}
            wrappedProps={{
                defaultActiveElement: PlanNameAndDescriptionConstants.nameTextFieldClassSelector,
                onDismiss: onClose,
                onSave,
                savingSpinnerLabel: Resources.SavingPlan,
                settingsViews,
                titleProps: { text: Resources.SettingsPanelTitle }
            }}
            wrappedType="ms.vss-work-web.wit-settings-panel"
        />
    );
};

function areMarkersDirty(currentMarkers: IMarkerSettingData[], originalMarkers: Marker[]) {
    if (currentMarkers.length !== originalMarkers.length) {
        return true;
    }

    return currentMarkers.findIndex((marker, index) => !compareMarkers(marker, originalMarkers[index])) >= 0;
}

function compareMarkers(currentMarker: IMarkerSettingData, originalMarker: Marker) {
    return (
        currentMarker.color.value.toLowerCase() === originalMarker.color?.toLowerCase() &&
        currentMarker.date.value === originalMarker.date &&
        currentMarker.label.value.toLowerCase() === originalMarker.label.toLowerCase()
    );
}

function getMarkerSettingData(markers: Marker[]): IMarkerSettingData[] {
    return markers.map(marker => {
        return {
            color: new ObservableValue(marker.color || BoardConstants.defaultColor),
            date: new ObservableValue(marker.date),
            id: newGuid(),
            label: new ObservableValue(marker.label)
        };
    });
}

function getTeamSelectedSettingData(teams: TimelineTeamData[]): ITeamSelectedSettingData[] {
    return teams.map(team => {
        return {
            backlogLevel: team.backlog.categoryReferenceName,
            project: team.projectId,
            team: team.id
        };
    });
}

function getPlanUpdate(
    plan: PlanData,
    name: string,
    description: string,
    fieldService: IWorkItemFieldService | IWorkItemFieldService2,
    fieldSettings: IFieldSettings,
    criteriaSettings: ICriteriaSelectedSettingData[],
    teamSettings: ITeamSelectedSettingData[],
    markerSettings: IMarkerSettingData[],
    styleSettings: IStylesRuleData[],
    tagStyleSettings: ITagStyleData[]
): UpdatePlan {
    const properties: DeliveryViewPropertyCollection = {
        cardSettings: getCardSettings(fieldService, fieldSettings),
        criteria: getFieldCriteriaAsFilterClauses(criteriaSettings),
        markers: getMarkerSettingsAsMarkers(markerSettings),
        teamBacklogMappings: getTeamSettingsAsTeamBacklogMapping(teamSettings),
        styleSettings: getStyleSettingsAsStyleRules(styleSettings),
        tagStyleSettings: getTagStyleSettingsAsRules(tagStyleSettings)
    };

    return {
        description,
        name,
        properties,
        revision: plan.revision,
        type: PlanType.DeliveryTimelineView
    };
}

function getCardSettings(fieldService: IWorkItemFieldService | IWorkItemFieldService2, fieldSettings: IFieldSettings): CardSettings {
    const additionalFields = fieldSettings.additionalFields.value
        .map(fieldSetting => {
            const field = fieldService.getFieldByReferenceName(fieldSetting.referenceName);
            if (field) {
                return {
                    fieldType: toWebApiFieldType(field.type),
                    referenceName: field.referenceName
                } as FieldInfo;
            }
        })
        .filter(field => field);

    const fields = {
        additionalFields
    } as CardFieldSettings;

    for (const fieldKey of Object.keys(fieldSettings.coreFields)) {
        const fieldValue = fieldSettings.coreFields[fieldKey].value;
        (fields as any)[fieldKey] = fieldValue;
    }

    return {
        fields
    };
}

/**
 * Keys correspond to CardFieldSettings properties
 */
const fieldResourceStrings = {
    showAssignedTo: Resources.ShowAssignedToLabel,
    showEmptyFields: Resources.ShowEmptyFieldsCheckboxLabel,
    showId: Resources.ShowIDLabel,
    showState: Resources.ShowStateLabel,
    showTags: Resources.ShowTagsLabel,
    showParent: Resources.ShowParentLabel,
    showChildRollup: Resources.ShowChildRollupLabel
};
